/**
 * Single-selector autopilot (S6 UX iteration): the right-pane tile-type cards
 * are the ONE type-choosing surface — the former left-pane suggestion row is
 * absorbed here. Pure helpers decide which card carries the "Suggested"
 * badge, which click applies a suggestion's auto-config, and whether a
 * completed preview may auto-select the best suggestion.
 */
import type { DashboardTileType } from '../../types/dashboardTypes';
import type { ShapeSuggestions, TileSuggestion } from '../../utils/shapeSuggest';

/**
 * Who set the current tile type in this dialog session:
 * - 'none'      — nothing chosen yet (fresh dialog, empty right pane)
 * - 'autopilot' — a preview's best suggestion was auto-applied
 * - 'user'      — an explicit pick (card click, edit mode's stored type,
 *                 template/AI insertion) — never overridden by autopilot
 */
export type TileTypeOrigin = 'none' | 'autopilot' | 'user';

/**
 * The suggestion a click on `type`'s card should apply — the best suggestion
 * wins over an alternative of the same type; null means a plain type set.
 */
export function suggestionForType(
  suggestions: ShapeSuggestions | null,
  type: DashboardTileType,
): TileSuggestion | null {
  if (!suggestions) return null;
  if (suggestions.best.tileType === type) return suggestions.best;
  return suggestions.alternatives.find((s) => s.tileType === type) ?? null;
}

/** The card carrying the sparkles "Suggested" badge — the best suggestion's type. */
export function suggestedTileType(suggestions: ShapeSuggestions | null): DashboardTileType | null {
  return suggestions?.best.tileType ?? null;
}

/**
 * Auto-select gating: a completed preview fills the empty right pane with the
 * best suggestion, but NEVER overrides an explicit pick — only when no type
 * was chosen yet ('none'), or the current type + config still came from the
 * autopilot itself ('autopilot' — a re-run preview may re-fit).
 */
export function shouldAutoApplyBest(origin: TileTypeOrigin): boolean {
  return origin !== 'user';
}
